<script setup lang="ts">
import { computed } from 'vue'

// At-a-glance orientation line for a review queue. Both /lint
// and /synthesis drop you into an N-card scroll with no sense of how
// big the backlog is or what's in it. This renders "N open — 3
// contradictions, 1 stale claim" from the already-loaded items, so it
// needs no extra fetch and stays in sync with the list below it.
//
// `segments` are kind buckets the caller computes from its own list;
// zero-count buckets are dropped here so the line only names what's
// actually present. Labels are the human-readable singular kind
// ("stale claim") — pluralisation is a naive +s, which is correct for
// every lint/synthesis kind we surface.

const props = defineProps<{
  count: number
  segments: { label: string; n: number }[]
}>()

const visible = computed(() => props.segments.filter((s) => s.n > 0))

function plural(label: string, n: number): string {
  return n === 1 ? label : `${label}s`
}
</script>

<template>
  <p class="text-sm text-white/70 m-0" data-testid="queue-summary">
    <span class="font-medium text-white">{{ count }}</span> open<template v-if="visible.length">
      —
      <span v-for="(s, i) in visible" :key="s.label">{{ i ? ', ' : '' }}{{ s.n }} {{ plural(s.label, s.n) }}</span>
    </template>
  </p>
</template>
